import * as React from "react";
import { useState } from "react";
import {
  Show,
  TextField,
  ImageField,
  BooleanField,
  TopToolbar,
  EditButton,
  useDataProvider,
  useRedirect,
  useNotify,
  Button,
  ReferenceField,
  ArrayField,
  DataTable,
  NumberField,
  FunctionField,
  SelectField,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  useRefresh,
  Confirm,
  usePermissions,
  useRecordContext,
  Title,
  LinearProgress,
  DateField,
} from "react-admin";
import { getTimeFromDate, shortWeekdayToLong } from "../utils/DateTimeUtils";
import Queue from "@mui/icons-material/Queue";
import IconQrCode from "@mui/icons-material/CropFree";
import IconDownload from "@mui/icons-material/ArrowDownward";
import IconLink from "@mui/icons-material/Link";
import IconUnlink from "@mui/icons-material/LinkOff";
import IconDeactivateProducts from "@mui/icons-material/NoMeals";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ROLE_TYPES } from "../enums/Roles";
import { AuditButton } from "../common/AuditButton";
import {
  GUIDE_RESTAURANT_TYPES,
  RESTAURANT_TYPES,
} from "../enums/RestaurantTypes";
import { PAYMENT_METHODS } from "../enums/PaymentMethods";
import { TabbedShowLayout } from "../common/TabbedShowLayout";
import VideoField from "../common/VideoField";
import { COUNTRIES } from "../enums/Countries";
import { PRODUCT_STATUS, PRODUCT_TYPES } from "../enums/ProductTypes";
import { RESTAURANT_SERVICE_TYPES } from "../enums/RestaurantServiceTypes";
import { exportProductsToTucan } from "../utils/TucanExportUtils";

const RestaurantShowActions = () => {
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const record = useRecordContext();
  const [MPDialogOpen, setMPDialogOpen] = useState(false);
  const [MPLink, setMPLink] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [unlinkMPDialogOpen, setUnlinkMPDialogOpen] = useState(false);
  const [deactivateProductsDialogOpen, setDeactivateProductsDialogOpen] =
    useState(false);

  const onDuplicateClick = () => {
    notify("Duplicando restaurante...");
    setIsLoading(true);
    dataProvider
      .duplicate("restaurants", { id: record.id })
      .then(({ data }) => {
        notify("Restaurante duplicado con éxito", { type: "success" });
        redirect("edit", "/restaurants", data?.id);
      })
      .catch((error) => {
        notify("Ha ocurrido un error duplicando el restaurante", {
          type: "error",
        });
      })
      .finally(() => setIsLoading(false));
  };

  const onExportTucanClick = async () => {
    notify("Exportando productos a Tucan...");
    setIsLoading(true);
    try {
      const { data: products } = await dataProvider.getList("products", {
        filter: { restaurant: record.id, status: [PRODUCT_STATUS.ACTIVE] },
        pagination: { page: 1, perPage: 9999 },
        sort: { field: "name", order: "ASC" },
      });
      // El backend solo filtra por un type, así que "no reward" se excluye en memoria.
      const exportable = products.filter(
        (product) => product.type !== PRODUCT_TYPES.REWARD
      );
      if (!exportable.length) {
        notify("No hay productos para exportar", { type: "warning" });
        return;
      }
      const categoryIds = [
        ...new Set(exportable.flatMap((product) => product.categories ?? [])),
      ];
      const { data: categories } = categoryIds.length
        ? await dataProvider.getMany("categories", { ids: categoryIds })
        : { data: [] };
      const categoriesById = Object.fromEntries(
        categories.map((category) => [category.id, category])
      );
      await exportProductsToTucan(exportable, categoriesById, record.name);
    } catch (error) {
      notify("Ha ocurrido un error exportando los productos", {
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onGenerateQRClick = () => {
    notify("Generando QR...");
    setIsLoading(true);
    dataProvider
      .generateQr("restaurants", { id: record.id })
      .then(({ data }) => {
        notify("QR generado con éxito", { type: "success" });
        downloadQR(data?.qr);
        refresh();
      })
      .catch((error) => {
        notify("Ha ocurrido un error generando el QR del restaurante", {
          type: "error",
        });
      })
      .finally(() => setIsLoading(false));
  };

  const onAssociateMercadoPagoClick = () => {
    notify("Generando Link de Mercado Pago...");
    setIsLoading(true);
    dataProvider
      .generateMercadoPagoLink("restaurants", { id: record.id })
      .then(({ data }) => {
        notify("Link de Mercado Pago generado con éxito", {
          type: "success",
        });
        setMPDialogOpen(true);
        setMPLink(data?.url);
      })
      .catch((error) => {
        notify(
          "Ha ocurrido un error generando el Link de Mercado Pago del restaurante",
          {
            type: "error",
          }
        );
      })
      .finally(() => setIsLoading(false));
  };

  const downloadQR = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.click();
  };

  const onUnlinkMPClick = () => {
    return dataProvider
      .unlinkMercadoPago("restaurants", { id: record.id })
      .then(({ data }) => {
        notify("El restaurante ha sido desasociado de Mercado Pago con éxito", {
          type: "success",
        });
      })
      .catch((error) => {
        notify(
          "Ha ocurrido un error desasociando el restaurante de Mercado Pago",
          {
            type: "error",
          }
        );
      })
      .finally(() => {
        setUnlinkMPDialogOpen(false);
        refresh();
      });
  };

  const onDeactivateProductsClick = async () => {
    notify("Desactivando productos del restaurante...");
    setIsLoading(true);

    try {
      const { data } = await dataProvider.deactivateRestaurantProducts(
        "restaurants",
        { id: record.id }
      );
      const success = data?.success;
      const successResults = data?.successResults;

      notify(
        `${successResults || 0} productos desactivados con éxito. ${
          !success
            ? "Algunos productos no han sido procesados correctamente"
            : ""
        }`,
        { type: success ? "success" : "warning" }
      );
    } catch (error) {
      notify("Ha ocurrido un error desactivando los productos", {
        type: "error",
      });
    } finally {
      setDeactivateProductsDialogOpen(false);
      setIsLoading(false);
    }
  };

  if (isLoading) return <LinearProgress />;

  return (
    <TopToolbar>
      <EditButton />
      {permissions === ROLE_TYPES.ADMIN && (
        <Button label="Duplicar" onClick={onDuplicateClick}>
          <Queue />
        </Button>
      )}
      {permissions === ROLE_TYPES.ADMIN && (
        <Button
          label="Desactivar productos"
          onClick={() => setDeactivateProductsDialogOpen(true)}
        >
          <IconDeactivateProducts />
        </Button>
      )}
      {permissions === ROLE_TYPES.ADMIN && (
        <Button label="Exportar Productos a Tucan" onClick={onExportTucanClick}>
          <FileDownloadIcon />
        </Button>
      )}
      {record?.type === RESTAURANT_TYPES.REGULAR &&
        (record?.qr ? (
          <Button label="Descargar QR" onClick={() => downloadQR(record.qr)}>
            <IconDownload />
          </Button>
        ) : (
          <Button label="Generar QR" onClick={onGenerateQRClick}>
            <IconQrCode />
          </Button>
        ))}
      {permissions === ROLE_TYPES.ADMIN && (
        <Button
          label={
            !record?.linkedToMercadoPago
              ? "Asociar Mercado Pago"
              : "Desasociar Mercado Pago"
          }
          onClick={
            !record?.linkedToMercadoPago
              ? onAssociateMercadoPagoClick
              : () => setUnlinkMPDialogOpen(true)
          }
        >
          {!record?.linkedToMercadoPago ? <IconLink /> : <IconUnlink />}
        </Button>
      )}
      {permissions === ROLE_TYPES.ADMIN && (
        <AuditButton />
      )}

      <Confirm
        isOpen={MPDialogOpen}
        loading={false}
        title={`Copiar link a continuación`}
        content={MPLink}
        onConfirm={() => setMPDialogOpen(false)}
        onClose={() => setMPDialogOpen(false)}
      />
      <Confirm
        isOpen={unlinkMPDialogOpen}
        loading={false}
        title={`Desasociar Mercado Pago`}
        content={"¿Estás seguro de desasociar Mercado Pago?"}
        onConfirm={onUnlinkMPClick}
        onClose={() => setUnlinkMPDialogOpen(false)}
      />
      <Confirm
        isOpen={deactivateProductsDialogOpen}
        loading={false}
        title={`Desactivar productos asociados`}
        content={"¿Estás seguro de desactivar todos los productos asociados?"}
        onConfirm={onDeactivateProductsClick}
        onClose={() => setDeactivateProductsDialogOpen(false)}
      />
    </TopToolbar>
  );
};

const RestaurantTitle = () => {
  const record = useRecordContext();
  return <Title title={`Restaurante "${record?.name}"`} />;
};

const RestaurantShow = () => {
  const { permissions } = usePermissions();
  const record = useRecordContext();

  return (
    <>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Datos básicos">
          {permissions === ROLE_TYPES.ADMIN && (
            <TextField source="id" label="ID" />
          )}
          <TextField source="name" label="Nombre" />
          <TextField source="description" label="Descripción" />
          <TextField source="address" label="Dirección" />
          <ReferenceField
            source="district"
            reference="districts"
            link={false}
            label="Barrio"
          >
            <TextField source="name" />
          </ReferenceField>
          <TextField source="location[1]" label="Latitud" />
          <TextField source="location[0]" label="Longitud" />
          <SelectField
            label="Tipo"
            source="type"
            choices={[
              { id: RESTAURANT_TYPES.REGULAR, name: "Regular" },
              { id: RESTAURANT_TYPES.GUIDE, name: "Guía" },
              { id: RESTAURANT_TYPES.PREGUIDE, name: "Pre-Guía" },
              { id: RESTAURANT_TYPES.DRAFT, name: "Borrador" },
            ]}
          />
          <ReferenceArrayField
            source="placeTypes"
            reference="place-types"
            label="Tipos de Lugar"
          >
            <SingleFieldList>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>

          <ReferenceArrayField
            source="placeTargets"
            reference="place-targets"
            label="Targets de Lugar"
          >
            <SingleFieldList>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>

          <FunctionField
            source="couponPaymentMethods"
            render={(record) =>
              `${record?.couponPaymentMethods
                ?.map((d) =>
                  d === PAYMENT_METHODS.CREDIT_CARD
                    ? "Tarjeta de Crédito"
                    : d === PAYMENT_METHODS.DEBIT_CARD
                      ? "Tarjeta de Débito"
                      : d === PAYMENT_METHODS.MERCADO_PAGO
                        ? "Mercado Pago"
                        : d === PAYMENT_METHODS.CASH
                          ? "Efectivo"
                          : ""
                )
                .join(", ")}`
            }
            label="Métodos de Pago (Cupones)"
          />

          {permissions === ROLE_TYPES.ADMIN && (
            <TextField source="googlePlaceId" label="Google Place ID" />
          )}

          <BooleanField source="active" label="Activo" />
          {permissions === ROLE_TYPES.ADMIN && (
            <BooleanField source="isOnboarding" label="En onboarding" />
          )}
          {record?.deactivatedAt && (
            <DateField
              source="deactivatedAt"
              label="Fecha de desactivación"
              showTime
            />
          )}

          <FunctionField
            source="serviceTypes"
            render={(record) =>
              `${record?.serviceTypes
                ?.map((d) =>
                  d === RESTAURANT_SERVICE_TYPES.DELIVERY
                    ? "Delivery"
                    : d === RESTAURANT_SERVICE_TYPES.STORE
                      ? "Local"
                      : ""
                )
                .join(", ")}`
            }
            label="Tipos de Servicio"
          />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Días y Horarios">
          <ArrayField source="timeTable" label="">
            <DataTable
              storeKey="restaurant.timeTable"
              rowClick={false}
              bulkActionButtons={false}
            >
              <DataTable.Col source="days" label="Días">
                <FunctionField
                  source="days"
                  render={(record) =>
                    `${record.days.map((d) => shortWeekdayToLong(d)).join(", ")}`
                  }
                  label="Días"
                />
              </DataTable.Col>
              <DataTable.Col source="times" label="">
                <ArrayField source="times" label="">
                  <DataTable
                    storeKey="restaurant.times"
                    rowClick={false}
                    bulkActionButtons={false}
                  >
                    <DataTable.Col source="opening" label="Apertura">
                      <FunctionField
                        source="opening"
                        render={(timeRecord) =>
                          getTimeFromDate(timeRecord.opening, record.country)
                        }
                        label="Apertura"
                      />
                    </DataTable.Col>
                    <DataTable.Col source="closing" label="Cierre">
                      <FunctionField
                        source="closing"
                        render={(timeRecord) =>
                          getTimeFromDate(timeRecord.closing, record.country)
                        }
                        label="Cierre"
                      />
                    </DataTable.Col>
                  </DataTable>
                </ArrayField>
              </DataTable.Col>
            </DataTable>
          </ArrayField>
        </TabbedShowLayout.Tab>

        {permissions === ROLE_TYPES.ADMIN &&
          record?.country &&
          COUNTRIES[record.country] === COUNTRIES.AR && (
            <TabbedShowLayout.Tab label="Facturación">
              <TextField label="Email" source="billingData.email" />
              <TextField label="CUIT" source="billingData.cuit" />
              <TextField label="Razón Social" source="billingData.name" />
              <TextField
                label="Domicilio fiscal"
                source="billingData.address"
              />
              <TextField label="Tipo documento" source="billingData.docType" />
              <TextField label="Provincia" source="billingData.province" />
              <TextField
                label="Condición frente al IVA"
                source="billingData.taxCondition"
              />
            </TabbedShowLayout.Tab>
          )}

        <TabbedShowLayout.Tab label="Compras">
          <FunctionField
            source="purchasePaymentMethods"
            render={(record) =>
              `${record?.purchasePaymentMethods
                ?.map((d) =>
                  d === PAYMENT_METHODS.CREDIT_CARD
                    ? "Tarjeta de Crédito"
                    : d === PAYMENT_METHODS.DEBIT_CARD
                      ? "Tarjeta de Débito"
                      : d === PAYMENT_METHODS.MERCADO_PAGO
                        ? "Mercado Pago"
                        : ""
                )
                .join(", ")}`
            }
            label="Métodos de Pago (Compras)"
          />
          {permissions === ROLE_TYPES.ADMIN && (
            <NumberField
              source="purchaseSplitPercentage"
              label="% Fee de Morfy"
              options={{
                style: "percent",
                maximumFractionDigits: 2,
              }}
            />
          )}
          <BooleanField source="enablePurchases" label="Compras Habilitadas" />
        </TabbedShowLayout.Tab>

        {record?.serviceTypes?.includes(RESTAURANT_SERVICE_TYPES.DELIVERY) && (
          <TabbedShowLayout.Tab label="Delivery">
            <NumberField
              source="integrations.tucan.storeId"
              label="Store ID en Tucan"
            />
            <TextField
              source="integrations.tucan.menuAlias"
              label="Menu Alias en Tucan"
            />
            {permissions === ROLE_TYPES.ADMIN && (
              <NumberField
                source="shippingDiscountPercentage"
                label="% Descuento Envío Morfy"
                options={{
                  style: "percent",
                  maximumFractionDigits: 2,
                }}
              />
            )}
          </TabbedShowLayout.Tab>
        )}
        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedShowLayout.Tab label="Premios">
            <ArrayField source="rewards" label="">
              <DataTable
                storeKey="restaurant.rewards"
                rowClick={false}
                bulkActionButtons={false}
              >
                <DataTable.Col source="product._id" label="Producto">
                  <ReferenceField
                    source="product._id"
                    reference="products"
                    label="Producto"
                    link={false}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </DataTable.Col>
                <DataTable.NumberCol
                  source="necessaryRedemptions"
                  label="Canjes Necesarios"
                />
                <DataTable.Col source="active" label="Activo">
                  <BooleanField source="active" label="Activo" />
                </DataTable.Col>
              </DataTable>
            </ArrayField>
          </TabbedShowLayout.Tab>
        )}
        <TabbedShowLayout.Tab label="Imágenes">
          <ArrayField source="coverImages" label="Imágenes de Cover">
            <DataTable
              storeKey="restaurant.coverImages"
              rowClick={false}
              bulkActionButtons={false}
            >
              <DataTable.Col source="url" label="">
                <ImageField source="url" label="" />
              </DataTable.Col>
            </DataTable>
          </ArrayField>
          <ImageField source="profileImage" label="Imagen de Perfil" />
          <ImageField source="mapImage" label="Imagen de Mapa" />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Videos">
          <ArrayField source="videos" label="Videos">
            <DataTable
              storeKey="restaurant.videos"
              rowClick={false}
              bulkActionButtons={false}
            >
              <DataTable.Col source="name" label="Nombre" />
              <DataTable.Col label="Video">
                <VideoField label="Video" />
              </DataTable.Col>
              <DataTable.Col source="thumbnail" label="Miniatura">
                <ImageField source="thumbnail" label="Miniatura" />
              </DataTable.Col>
              <DataTable.Col source="active" label="Activo">
                <BooleanField source="active" label="Activo" />
              </DataTable.Col>
            </DataTable>
          </ArrayField>
        </TabbedShowLayout.Tab>
        {permissions === ROLE_TYPES.ADMIN &&
          GUIDE_RESTAURANT_TYPES.includes(record?.type) && (
            <TabbedShowLayout.Tab label="Productos (Guía)">
              <ArrayField source="productImages" label="Imágenes de Productos">
                <DataTable
                  storeKey="restaurant.productImages"
                  rowClick={false}
                  bulkActionButtons={false}
                >
                  <DataTable.Col source="url" label="">
                    <ImageField source="url" label="" />
                  </DataTable.Col>
                </DataTable>
              </ArrayField>
            </TabbedShowLayout.Tab>
          )}
        {permissions === ROLE_TYPES.ADMIN && (
          <TabbedShowLayout.Tab label="Suscripción">
            <ReferenceField
              source="subscriptionPlan"
              reference="restaurant-subscription-plans"
              label="Plan"
            >
              <TextField source="name" />
            </ReferenceField>
          </TabbedShowLayout.Tab>
        )}
      </TabbedShowLayout>
    </>
  );
};

const RestaurantShowContainer = () => {
  return (
    <Show title={<RestaurantTitle />} actions={<RestaurantShowActions />}>
      <RestaurantShow />
    </Show>
  );
};

export default RestaurantShowContainer;
